import { getSession, signIn } from 'next-auth/react'
import {
  DEMO_STATE_GENERATION_HEADER,
  MAINTENANCE_MESSAGE,
  createGenerationTracker,
  isMaintenanceResponse,
  parseRetryAfter,
  type DemoStateEvent,
} from '@/lib/demo-state'

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''

/** Request headers carrying the Country / Industry view (contract §9-§10). */
export const VIEW_HEADER_COUNTRY = 'X-AP-Country'
export const VIEW_HEADER_INDUSTRY = 'X-AP-Industry'

let viewHeaders: { country: string; industry: string } | null = null

type ClientSession = Awaited<ReturnType<typeof getSession>>

/** Reuse a session for this long, and never closer than the margin to token expiry. */
const SESSION_REUSE_MS = 30_000
const SESSION_EXPIRY_MARGIN_MS = 75_000

let cachedSession: { session: ClientSession; fetchedAt: number } | null = null
let sessionRequest: Promise<ClientSession> | null = null

/**
 * One `/api/auth/session` round trip serves every request fired together.
 * Without this, a page that loads ten endpoints asked the Next.js server for
 * the session ten times, and those calls queued (and each could refresh the
 * token with Keycloak), delaying every request by seconds.
 */
async function currentSession(forceFresh = false): Promise<ClientSession> {
  const now = Date.now()
  if (!forceFresh && cachedSession) {
    const expires = cachedSession.session?.accessTokenExpires ?? 0
    if (now - cachedSession.fetchedAt < SESSION_REUSE_MS && expires - now > SESSION_EXPIRY_MARGIN_MS) {
      return cachedSession.session
    }
  }
  if (!sessionRequest) {
    sessionRequest = getSession()
      .then((session) => {
        cachedSession = { session, fetchedAt: Date.now() }
        return session
      })
      .finally(() => {
        sessionRequest = null
      })
  }
  return sessionRequest
}

/**
 * Set by `CountryProvider` whenever the view changes. The backend reads the
 * stored preference itself; these headers can only narrow it, so a stale value
 * can never widen what a user sees.
 */
export function setViewHeaders(view: { country: string; industry: string } | null): void {
  viewHeaders = view
}

/**
 * An API error that keeps the HTTP status, so a caller can tell a 404 (not
 * yours), 403 (role not set up) and 409 (stale version) apart. It is still an
 * `Error` with the server's `detail` as the message, so existing callers that
 * only read `message` are unaffected.
 */
export class ApiError extends Error {
  readonly status: number
  /** The parsed error body, for callers that need more than `detail` (e.g. a 409's `run_id`). */
  readonly body: unknown

  constructor(message: string, status: number, body: unknown = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

type DemoStateListener = (event: DemoStateEvent) => void

const demoStateListeners = new Set<DemoStateListener>()
const generationTracker = createGenerationTracker()

/**
 * Listen for the Master Reset signals every response carries (maintenance `503`, a changed
 * `X-Demo-State-Generation`). Returns the unsubscribe function. Callers still get their own
 * errors as before; this only lets one place in the shell tell the user what happened.
 */
export function subscribeDemoState(listener: DemoStateListener): () => void {
  demoStateListeners.add(listener)
  return () => {
    demoStateListeners.delete(listener)
  }
}

function emitDemoState(event: DemoStateEvent): void {
  for (const listener of demoStateListeners) {
    try {
      listener(event)
    } catch {
      // A broken listener must never fail the request that triggered it.
    }
  }
}

function observeGeneration(response: Response): void {
  const change = generationTracker.observe(response.headers.get(DEMO_STATE_GENERATION_HEADER))
  if (change) emitDemoState({ type: 'generation-changed', ...change })
}

/** Status of an error thrown by the API client, or null for a network or other error. */
export function apiErrorStatus(err: unknown): number | null {
  return err instanceof ApiError ? err.status : null
}

function detailMessage(detail: unknown): string {
  if (typeof detail === 'string' && detail) return detail
  // FastAPI 422 bodies carry a list of {loc, msg}; show the messages, not "[object Object]".
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : ''))
      .filter(Boolean)
    if (parts.length) return parts.join('; ')
  }
  // Object details, e.g. the Auto bridge mirror check {detail, field} or {message}.
  if (detail && typeof detail === 'object') {
    const record = detail as { detail?: unknown; message?: unknown; field?: unknown }
    const text = typeof record.detail === 'string' ? record.detail : typeof record.message === 'string' ? record.message : ''
    if (text) return typeof record.field === 'string' && record.field ? `${text} (${record.field})` : text
  }
  return 'An API error occurred.'
}

/**
 * A robust API client that handles authentication and base path resolution.
 * @param endpoint The API endpoint to call, e.g., '/api/test' or '/api/admin/dashboard'.
 *                 The endpoint should include the '/api' prefix.
 * @param options Standard fetch options (method, body, etc.).
 */
async function apiClientFetch<T = unknown>(
  endpoint: string,
  options: RequestInit = {},
  retried = false
): Promise<T> {
  const session = await currentSession(retried)

  const headers = new Headers(options.headers || {})

  if (session?.accessToken) {
    headers.set('Authorization', `Bearer ${session.accessToken}`)
  }

  if (viewHeaders) {
    if (!headers.has(VIEW_HEADER_COUNTRY)) headers.set(VIEW_HEADER_COUNTRY, viewHeaders.country)
    if (!headers.has(VIEW_HEADER_INDUSTRY)) headers.set(VIEW_HEADER_INDUSTRY, viewHeaders.industry)
  }

  // Handle session errors that occurred during a token refresh attempt.
  if (session?.error === 'RefreshAccessTokenError') {
    // Force sign-in to get a new, valid session.
    await signIn('keycloak')
    // Throw an error to stop the current, failing API call.
    throw new Error('Session expired. Please sign in again.')
  }

  // Construct the full URL: http://localhost:8001/app1/api/test
  const fullUrl = `${API_URL}${BASE_PATH}${endpoint}`

  const response = await fetch(fullUrl, { ...options, headers })
  observeGeneration(response)

  // If the backend returns a 401, it means the token is invalid or expired.
  // This can happen if the backend is restarted or the user's session is revoked.
  // We trigger a re-login to fix the session.
  if (response.status === 401) {
    // The reused token may have been refreshed since; retry once with a fresh session.
    if (!retried) return apiClientFetch<T>(endpoint, options, true)
    await signIn('keycloak')
    throw new Error('Your session has expired. Please sign in again.')
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: response.statusText,
    }))
    const message = detailMessage(errorData?.detail)
    const retryAfter = response.headers.get('Retry-After')
    const rawDetail = typeof errorData?.detail === 'string' ? errorData.detail : null
    if (isMaintenanceResponse(response.status, retryAfter, rawDetail)) {
      emitDemoState({
        type: 'maintenance',
        message: rawDetail || MAINTENANCE_MESSAGE,
        retryAfterSeconds: parseRetryAfter(retryAfter),
      })
    }
    throw new ApiError(message, response.status, errorData)
  }

  // Handle responses with no content
  if (response.status === 204) {
    return null as T
  }

  return response.json() as Promise<T>
}

/**
 * API client with convenience methods for common HTTP operations.
 */
export const apiClient = {
  /**
   * Perform a GET request.
   */
  get: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'GET' })
  },

  /**
   * Perform a POST request.
   */
  post: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PUT request.
   */
  put: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a PATCH request.
   */
  patch: <T = unknown>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    })
  },

  /**
   * Perform a file upload (multipart/form-data POST).
   * NOTE: Do NOT set Content-Type — the browser sets it with the correct boundary.
   */
  upload: <T = unknown>(endpoint: string, formData: FormData, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
    })
  },

  /**
   * Perform a DELETE request.
   */
  delete: <T = unknown>(endpoint: string, options?: RequestInit): Promise<T> => {
    return apiClientFetch<T>(endpoint, { ...options, method: 'DELETE' })
  },
}

// Default export for backward compatibility
export default apiClientFetch
